const inHouseUxPractice = {
  id: "in-house-ux-practice",
  slug: "in-house-ux-practice",
  category: "Foundations",
  navTitle: "Establishing the In-House UX Practice",
  title: "Establishing the In-House UX Practice",
  introduction: [
    "Co-operators was expanding its digital services, but much of the UX work still depended on agencies and contractors. I was brought in to build an internal multidisciplinary practice that could support the work already underway while creating stronger capability inside the organization.",
  ],
  challenge: [
    {
      title: "Limited internal expertise",
      paragraphs: [
        "The organization had talented people working across different initiatives, but UX knowledge was fragmented and often tied to external partners. That made it harder to retain knowledge, maintain continuity, and build a consistent way of working.",
      ],
    },
    {
      title: "Growing and unpredictable demand",
      paragraphs: [
        "Demand for UX support was increasing, but not every project needed the same skills or the same level of involvement. The team needed to grow without hiring too quickly or rebuilding the practice every time priorities changed.",
      ],
    },
    {
      title: "Fragmented tools and workflows",
      paragraphs: [
        "Designers were working across Sketch, Zeplin, Adobe XD, Miro, and other tools. Files were scattered, handoffs were inconsistent, and the setup created unnecessary cost and friction.",
      ],
    },
    {
      title: "Keeping the practice connected",
      paragraphs: [
        "Designers were embedded within product squads, which was the right model for delivery. The risk was that they could become isolated from one another and start operating as individual production resources rather than as part of a shared practice.",
      ],
    },
  ],
  process: [
    {
      title: "Designing the practice",
      paragraphs: [
        "I started with a deliberately small, balanced team rather than hiring several people into the same discipline.",
      ],
      bullets: [
        "Built the initial practice around UX, UI, research, strategy, and content design.",
        "Expanded as demand became clearer, eventually leading a team of eight full-time employees and approximately eight contractors.",
        "Used contractors to respond to immediate project needs, then converted strong performers into permanent roles when the demand justified it.",
        "Introduced Macs and consolidated Sketch, Adobe XD, Zeplin, and Miro into Figma and FigJam.",
        "Redirected some of the licence savings into UserTesting so the team could gather more direct customer evidence.",
      ],
      closing:
        "This allowed the practice to grow responsibly instead of treating headcount as the first answer.",
    },
    {
      title: "Building the operating model",
      paragraphs: [
        "Designers worked within product squads, but I created shared routines so the team still operated as one practice.",
      ],
      bullets: [
        "Held a weekly UX stand-up separate from project and sprint meetings.",
        "Used bi-weekly one-on-ones for coaching, development, and support.",
        "Introduced critiques, peer-led learning, and short design exercises.",
        "Encouraged conferences, webinars, training, and certification.",
        "Asked more experienced team members to share specialist skills and mentor others.",
      ],
      closing:
        "The goal was to give people room to work independently without allowing the practice to become disconnected.",
    },
    {
      title: "Planning for growth",
      paragraphs: [
        "I moved UX capacity planning into earlier portfolio conversations.",
      ],
      bullets: [
        "Participated in planning meetings to understand upcoming initiatives, likely effort, and required skills.",
        "Assessed whether the existing team could absorb the work or whether additional support was needed.",
        "Used the likely duration of the work to decide between contractors and permanent roles.",
        "Set expectations before projects entered delivery rather than waiting until the team was already constrained.",
      ],
      closing:
        "Over time, UX involvement in planning became expected rather than something I had to request.",
    },
  ],
  outcomes: [
    {
      title: "A scalable UX practice",
      description:
        "The team grew from a small core into approximately sixteen employees and contractors supporting several initiatives at once.",
    },
    {
      title: "Stronger design operations",
      description:
        "Shared tools, clearer workflows, and earlier capacity planning reduced friction and made the practice easier to manage.",
    },
    {
      title: "A connected and increasingly autonomous team",
      description:
        "Designers remained connected while embedded across different squads. The team became more confident, more self-directed, and less dependent on every decision passing through me.",
    },
    {
      title: "A trusted organizational partner",
      description:
        "UX moved from being a requested service to becoming part of product and delivery planning.",
    },
  ],
  reflection: [
    "Building the practice reinforced that a strong design team does not emerge simply because talented people have been hired: the team itself needs to be designed.",
    "Hiring talented people is only the beginning. I found myself spending just as much time thinking about the mix of disciplines, how people worked together, how knowledge was shared, and how to plan for growth as I did building the team itself. As those pieces came together, the practice earned the trust of the organization and became increasingly capable of delivering high-quality work without relying on constant direction.",
  ],
};

export default inHouseUxPractice;
